import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import unifiedApi from '../../utils/unifiedApi';
import { useIsAdmin, usePermissions } from '../../contexts/PermissionsContext';

const LeagueForm = () => {
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { isLoading: permissionsLoading } = usePermissions();

  useEffect(() => {
    if (!permissionsLoading && !isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isAdmin, permissionsLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        name,
        isActive,
        startDate: startDate ? `${startDate}T00:00:00Z` : null,
        endDate: endDate ? `${endDate}T00:00:00Z` : null,
      };

      await unifiedApi.createLeague(payload);
      navigate('/admin/leagues');
    } catch (err) {
      setError('Failed to create league. Please try again.');
      console.error('Failed to create league', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Create New League
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="League Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="success"
                />
              }
              label={isActive ? 'Active' : 'Archived'}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={saving || !name.trim()}
              >
                {saving ? <CircularProgress size={24} /> : 'Create League'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/leagues')}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </AdminLayout>
  );
};

export default LeagueForm;
